import { FunctionComponent, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SubAccountSearchForm from './SubAccountSearchForm';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useCountryLabels from '@/hooks/useCountryLabels';
import { TableFiltersContainer, Button, Table, TransactionStatus } from 'UI';
import styles from './SubAccountsList.module.scss';
import {
  formatAsCurrency,
  CurrencySymbols,
  Box,
  AccountBalance,
  TableProps,
  AccountDetailsMap,
  GetColorStatus,
} from 'vopay';

type SubAccountsListProps = {
  subAccountsList: AccountBalance[];
  details: AccountDetailsMap;
  exportCSV: (accountId: string | undefined) => void;
};

type SearchValues = { searchTerm: string; country: string };

type FC = FunctionComponent<SubAccountsListProps>;

const SubAccountsList: FC = (props: SubAccountsListProps) => {
  const { subAccountsList, details, exportCSV } = props;
  const [updatedList, setUpdatedList] = useState<AccountBalance[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [shouldGetCountryList, setShouldGetCountryList] = useState(true);
  const [filteredLists, setFilteredLists] = useState<AccountBalance[]>([]);
  const [searchValues, setSearchValues] = useState<SearchValues>({ searchTerm: '', country: '' });
  const [filtersApplied, setFiltersApplied] = useState(false);

  const { accountId } = useParams<{ accountId: string }>();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.SubAccounts' });
  const { t: tStatus } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });
  const { resolveCountryLabel } = useCountryLabels();

  useEffect(() => {
    if (subAccountsList.length > 0) {
      setUpdatedList(subAccountsList);
    }

    if (subAccountsList.length > 0 && shouldGetCountryList) {
      const uniqueCurrencies = [...new Set(subAccountsList.map((item) => item.Country))].sort((a, b) =>
        a === 'Canada' ? -1 : String(a).localeCompare(String(b)),
      );

      setShouldGetCountryList(false);
      setCountries(uniqueCurrencies);
    }
  }, [subAccountsList]);

  useEffect(() => {
    const keys = ['AccountName', 'DBAName'];
    const { searchTerm, country } = searchValues;
    setFilteredLists(
      updatedList.filter(
        (data: AccountBalance) =>
          (country === '' || country === 'All' || data.Country === country) &&
          (searchTerm === '' ||
            keys.some((key) =>
              data[key as keyof AccountBalance]?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
            )),
      ),
    );
  }, [searchValues, updatedList]);

  const handleSearch = (values: SearchValues) => {
    setSearchValues(values);
    setFiltersApplied(values.searchTerm !== '' || (values.country !== '' && values.country !== 'All'));
  };

  const tableProps: TableProps<AccountBalance> = {
    id: 'sub-account-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'accountName',
        header: t('Table.accountName'),
        align: 'left',
        template: ({ data }: { data: AccountBalance }) => (
          <Table.Link
            to={{
              pathname: `sub-account/${data.AccountID}`,
              search: new URLSearchParams({
                accountId: data.AccountID,
                isActive: data.IsActive.toString(),
                accountStatus: data.AccountStatus,
                currency: data.Currency,
                pending: data.PendingBalance.toString(),
                available: data.AvailableBalance.toString(),
                account: data.AccountBalance.toString(),
                reserve: data.Reserve.toString(),
                immediate: data.AvailableImmediately.toString(),
              }).toString(),
            }}
          >
            <div>
              {data.AccountName}
              {data.DBAName && (
                <>
                  <br />
                  <i>{data.DBAName}</i>
                </>
              )}
            </div>
          </Table.Link>
        ),
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left' as const,
        template: ({ data }: { data: AccountBalance }) => (
          <Box jc="fs">
            {data.IsActive ? (
              <TransactionStatus status="active" label={tStatus('Active')} />
            ) : (
              !!data.AccountStatus && (
                <TransactionStatus status={GetColorStatus(data.AccountStatus)} label={tStatus(data.AccountStatus)} />
              )
            )}
          </Box>
        ),
      },
      {
        id: 'country',
        header: t('Table.country'),
        align: 'left' as const,
        template: ({ data }: { data: AccountBalance }) => <>{resolveCountryLabel(data.Country)}</>,
      },
      {
        id: 'pending',
        header: t('Table.pending'),
        align: 'left' as const,
        template: ({ data }: { data: AccountBalance }) =>
          `${data.Currency} ${formatAsCurrency(data.PendingBalance, CurrencySymbols[data.Currency])}`,
      },
      {
        id: 'available',
        header: t('Table.available'),
        align: 'left' as const,
        template: ({ data }: { data: AccountBalance }) =>
          `${data.Currency} ${formatAsCurrency(data.AvailableBalance, CurrencySymbols[data.Currency])}`,
      },
    ],
    rows: filteredLists.map((balance) => ({
      id: balance.AccountID,
      data: balance,
    })),
  };

  return (
    <div>
      <TableFiltersContainer filtersApplied={filtersApplied} open={false}>
        <div className={styles.trigger}>
          <TableFiltersContainer.Trigger />
          <Button
            id="sub-accounts-ExportCSV"
            type="button"
            colorScheme="secondary"
            size="small"
            disabled={isRoleViewOnly}
            onClick={() => exportCSV(details[accountId]?.accountId || '')}
          >
            {t('exportCSVButton')}
          </Button>
        </div>
        <TableFiltersContainer.Content>
          <SubAccountSearchForm countries={countries} onSearch={handleSearch} />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>
      <div className={styles.tableContainer}>
        <Table {...tableProps} />
      </div>
    </div>
  );
};

export default SubAccountsList;
